import { useSetAtom } from 'jotai';
import React, { useEffect, useRef } from 'react';

import { StreamType } from '@smart-tv/typings/content';

import useStream from '../../_hooks/use-stream';
import { descalePlayerAtom } from '../../atoms';
import { StreamParams } from '../../types';
import { CmdError } from '@smart-tv/contexts/error-context/error-context';
import usePlayer from '../../use-player';
import { usePlayerContext } from '../../_contexts/player-context';
import { SESSION_STORAGE_SHEET_KEY } from '@smart-tv/components/program-sheet/_constants/session-storage';
import { pushBackKeyLayer } from '@smart-tv/utils/back-key-layers';

export interface StreamPlayerContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface StreamPlayerContextValues {
  isPlaying: boolean;
  playerIsClosing: boolean;
  playerIsClosingToSheet: boolean;
  isHandlingGetStream: boolean;
  handleOpen: (streamOptions: StreamOptions, trackingOptions?: TrackingOptions) => void;
  handleClose: (returningToSheet?: boolean) => Promise<void>;
  changeLiveChannel: (streamParams: Partial<StreamParams>) => void;
  streamOptions?: StreamOptions;
  error?: CmdError;
}

export interface StreamOptions {
  title?: string;
  duration?: number;
  episodeNumber?: number;
  seasonNumber?: number;
  startTime?: Date;
  streamParams: StreamParams;
  streamType: StreamType;
  year?: number;
  genre?: string;
}

export interface TrackingOptions {
  streamType: StreamType;
  title?: string;
  trackingCategory?: string;
  chosenProvider?: string;
  genres?: string[];
}

export const StreamPlayerContext = React.createContext<StreamPlayerContextValues>(
  {} as StreamPlayerContextValues
);

export const StreamPlayerConsumer = StreamPlayerContext.Consumer;

export const StreamPlayerProvider = (props: StreamPlayerContextProps): JSX.Element => {
  const { children } = props;

  const { handleSetStreamType, setError: setStreamError } = useStream();
  const { openStream, closeStream, error, setError: setPlayerError } = usePlayer({});
  const { onSetFirstTimeLoading } = usePlayerContext();

  const [isPlaying, setIsPlaying] = React.useState(false);
  const [playerIsClosing, setPlayerIsClosing] = React.useState(false);
  const [playerIsClosingToSheet, setPlayerIsClosingToSheet] = React.useState(false);
  const [isHandlingGetStream, setIsHandlingGetStream] = React.useState(false);

  const [streamOptions, setStreamOptions] = React.useState<StreamOptions>();
  const previousStreamParamsRef = useRef<StreamParams>();
  // Guards handleClose itself so every caller (Escape handlers in live/vod player
  // screens, PlayerBackButton, use-player-content's local handler) is protected
  // against a rapid second invocation closing the stream twice.
  const isClosingRef = useRef(false);

  useEffect(() => {
    if (error) {
      setStreamError(error);
    }
  }, [error, setStreamError]);

  const setDescalePlayer = useSetAtom(descalePlayerAtom);

  const handleOpen = React.useCallback(
    async (streamOptions: StreamOptions) => {
      if (isHandlingGetStream) return;
      // block back key presses while opening the stream
      const removeBackKeyLayer = pushBackKeyLayer(() => undefined);
      setIsHandlingGetStream(true);

      const { streamParams, streamType } = streamOptions;
      const { sid, eventId, mediaId, progress } = streamParams;

      setPlayerError(undefined);
      setStreamError(null);

      if (previousStreamParamsRef.current) {
        await closeStream(previousStreamParamsRef.current);
      }

      handleSetStreamType(streamType);

      const formattedStreamOptions = {
        ...streamOptions,
        streamParams: {
          ...(sid && { sid }),
          ...(eventId && { eventId }),
          ...(mediaId && { mediaId }),
          ...(progress !== undefined && progress !== null && { progress })
        }
      };

      try {
        previousStreamParamsRef.current = formattedStreamOptions.streamParams;
        setIsPlaying(true);

        setStreamOptions(formattedStreamOptions);
        await openStream(formattedStreamOptions.streamParams);
      } finally {
        setIsHandlingGetStream(false);
        removeBackKeyLayer?.();
      }
    },
    [
      handleSetStreamType,
      setIsPlaying,
      setStreamOptions,
      openStream,
      closeStream,
      setPlayerError,
      setStreamError,
      isHandlingGetStream
    ]
  );

  useEffect(() => {
    return () => {
      if (previousStreamParamsRef.current) {
        closeStream(previousStreamParamsRef.current);
        previousStreamParamsRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for app-exiting-cleanup event (triggered by exit key) to close stream before app terminates
  useEffect(() => {
    const handleAppExit = () => {
      if (previousStreamParamsRef.current) {
        closeStream(previousStreamParamsRef.current);
      }
    };
    window.addEventListener('app-exiting-cleanup', handleAppExit);
    return () => window.removeEventListener('app-exiting-cleanup', handleAppExit);
  }, [closeStream]);

  const handleClose = React.useCallback(
    async (returningToSheet = false) => {
      if (isClosingRef.current) return;
      isClosingRef.current = true;
      setPlayerIsClosing(true);
      setPlayerIsClosingToSheet(returningToSheet);
      try {
        if (previousStreamParamsRef.current) {
          await closeStream(previousStreamParamsRef.current);
        }
      } finally {
        isClosingRef.current = false;
        sessionStorage.removeItem(SESSION_STORAGE_SHEET_KEY);
        setDescalePlayer(false);
        setIsPlaying(false);
        setPlayerIsClosing(false);
        setPlayerIsClosingToSheet(false);
        previousStreamParamsRef.current = undefined;
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => undefined);
        }
      }
    },
    [setDescalePlayer, setIsPlaying, closeStream]
  );

  const changeLiveChannel = React.useCallback(
    async (newParams: Partial<StreamParams>) => {
      if (!streamOptions) return;
      if (streamOptions.streamParams.sid === newParams.sid) return;
      const newStreamOptions = {
        ...streamOptions,
        streamParams: {
          ...streamOptions.streamParams,
          ...newParams
        }
      };
      await handleOpen(newStreamOptions);
      setStreamOptions(newStreamOptions);
      onSetFirstTimeLoading(true);
    },
    [streamOptions, handleOpen, onSetFirstTimeLoading]
  );

  const contextValues = React.useMemo(
    () => ({
      isPlaying,
      playerIsClosing,
      playerIsClosingToSheet,
      isHandlingGetStream,
      handleOpen,
      handleClose,
      changeLiveChannel,
      streamOptions
    }),
    [
      isPlaying,
      playerIsClosing,
      playerIsClosingToSheet,
      isHandlingGetStream,
      handleOpen,
      handleClose,
      changeLiveChannel,
      streamOptions
    ]
  );

  return (
    <StreamPlayerContext.Provider value={contextValues}>{children}</StreamPlayerContext.Provider>
  );
};

export default StreamPlayerContext;
